import React, { useContext, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import Accordion from 'react-bootstrap/Accordion'
import Col from 'react-bootstrap/Col'
import RoleDropdown from 'duties/filters/role-dropdown'
import { useRoleComparison } from 'api/hooks/use-role-comparisons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faFolder,
  faUser,
  faExclamationCircle,
  faPowerOff,
  faLayerGroup,
  faRandom,
  faTable,
  faCircle
} from '@fortawesome/free-solid-svg-icons'
import { mapAccessType } from 'duties/data-accesses/helper'
import SodExport from 'duties/shared/Export'
import UserContext from 'context/user-context'

const SummaryTable = ({ stats }) => {
  return (
    <table className="table table-border mt-3">
      <tbody>
        {stats.map((stat) => (
          <tr key={stat.name}>
            <td>
              {stat.icon}
              <span className="text-muted ms-1">{stat.value}</span>
              <span className="text-muted ms-1">{stat.name}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function CompareRoles({ setShow, show, role, roleId }) {
  const { municipality } = useContext(UserContext)
  const [params, setParams] = useState({})
  const [modalOptions, setModalOptions] = useState({
    showOverlappingPermissions: false
  })
  const handleClose = () => {
    setShow(false)
    setParams({})
  }

  const { data, isSuccess } = useRoleComparison({
    params: {
      role_id: roleId,
      comparison_id: params.role_id
    },
    options: {
      enabled: !!params.role_id
    }
  })

  // Memoized so SodExport's params-sync effect doesn't re-fire every render.
  // include_overlapping mirrors the toggle, so the CSV matches what's on screen.
  const exportParams = useMemo(
    () => ({
      role_id: roleId,
      comparison_id: params.role_id,
      include_overlapping: modalOptions.showOverlappingPermissions
    }),
    [roleId, params.role_id, modalOptions.showOverlappingPermissions]
  )

  function groupAndSortPermissionsByCategory(
    permissionsUnion,
    permissionsDiff
  ) {
    const groupPermissions = (permissions, type) => {
      return permissions.reduce((acc, permission) => {
        const key = permission.category_caption || 'Uncategorized'
        if (!acc[key]) acc[key] = { Union: [], Diff: [] }
        acc[key][type].push(permission)
        return acc
      }, {})
    }

    const groupedUnion = groupPermissions(permissionsUnion, 'Union')
    const groupedDiff = groupPermissions(permissionsDiff, 'Diff')

    // Merge Union and Diff groups
    const mergedGroups = { ...groupedUnion }
    Object.keys(groupedDiff).forEach((key) => {
      if (!mergedGroups[key]) mergedGroups[key] = { Union: [], Diff: [] }
      mergedGroups[key].Diff.push(...groupedDiff[key].Diff)
    })

    // Sort each group by permission description
    Object.keys(mergedGroups).forEach((key) => {
      Object.keys(mergedGroups[key]).forEach((type) => {
        mergedGroups[key][type].sort((a, b) =>
          a.description.localeCompare(b.description)
        )
      })
    })

    // Sort groups alphabetically by category
    return Object.keys(mergedGroups)
      .sort((a, b) => a.localeCompare(b))
      .reduce((sortedAcc, key) => {
        sortedAcc[key] = mergedGroups[key]
        return sortedAcc
      }, {})
  }

  function groupDataAccessByTopModuleAndName(dataAccess) {
    return dataAccess.reduce((acc, da) => {
      const topModuleKey = da.data_access.top_module_name || 'Uncategorized'
      const nameKey = da.data_access.name || 'Unnamed'

      if (!acc[topModuleKey]) acc[topModuleKey] = {}
      if (!acc[topModuleKey][nameKey]) acc[topModuleKey][nameKey] = []

      const existingEntry = acc[topModuleKey][nameKey].find(
        (entry) =>
          entry.access_type === da.access_type &&
          entry.start1 === da.start1 &&
          entry.end1 === da.end1
      )

      if (existingEntry) {
        existingEntry.program.push(da.program_name || da.program_id?.toString())
      } else {
        acc[topModuleKey][nameKey].push({
          program: [da.program_name || da.program_id?.toString()],
          access_type: da.access_type,
          start1: da.start1,
          end1: da.end1
        })
      }

      return acc
    }, {})
  }

  function groupEmployeesByDepartment(employees) {
    return employees.reduce((acc, employee) => {
      const key = employee.department_name || 'Uncategorized'
      if (!acc[key]) acc[key] = []
      acc[key].push(employee)
      return acc
    }, {})
  }

  const getScoreColor = (score) => {
    if (score >= 60) {
      return '#E77152'
    } else if (score >= 40) {
      return '#fd7e14'
    } else if (score >= 20) {
      return '#E9C46A'
    } else {
      return '#606060'
    }
  }

  const stats = (role) => {
    return [
      {
        name: 'Score',
        icon: (
          <FontAwesomeIcon
            icon={faCircle}
            style={{ color: getScoreColor(role.score) }}
          />
        ),
        value: role.score
      },
      {
        name: 'Internal Conflicts',
        icon: (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-danger"
          />
        ),
        value: role.internal_conflicts_count
      },
      {
        name: 'Users (Enabled)',
        icon: <FontAwesomeIcon icon={faUser} className="text-primary" />,
        value: role.employees_count
      },
      {
        name: 'High Risk Permissions',
        icon: (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="text-warning"
          />
        ),
        value: role.high_risk_permissions_count
      },
      {
        name: 'Super User Permissions',
        icon: <FontAwesomeIcon icon={faPowerOff} className="text-danger" />,
        value: role.superuser_permissions_count
      },
      {
        name: 'Business Rules',
        icon: <FontAwesomeIcon icon={faRandom} className="text-primary" />,
        value: role.business_rules_count
      },
      {
        name: 'Functional Categories',
        icon: <FontAwesomeIcon icon={faLayerGroup} className="text-primary" />,
        value: role.functional_categories_count
      },
      {
        name: 'Data Access',
        icon: <FontAwesomeIcon icon={faTable} className="text-primary" />,
        value: role.data_access_count
      }
    ]
  }

  const PermissionsAccordion = ({
    union,
    diff,
    groupFunction,
    title,
    showOverlapping
  }) => {
    return (
      <>
        {isSuccess && Object.keys(groupFunction(union, diff)).length > 0 && (
          <div className="mt-2">
            <Accordion alwaysOpen>
              {Object.entries(groupFunction(union, diff)).map(
                ([category, { Union, Diff }], index) => (
                  <Accordion.Item eventKey={index.toString()} key={category}>
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faFolder} className="me-2" />
                      {category}{' '}
                      <small className="text-muted ms-4">
                        Differences: {Diff.length}
                      </small>
                    </Accordion.Header>
                    <Accordion.Body>
                      {showOverlapping && (
                        <div>
                          <strong>Overlapping Permissions:</strong>
                          <ul className="ps-3 list-disc text-success">
                            {Union.map((item) => (
                              <li key={`union-${item.id}`}>
                                {item.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3">
                        <strong>Differences:</strong>
                        <ul className="ps-3 list-disc">
                          {Diff.map((item) => (
                            <li key={`diff-${item.id}`}>{item.description}</li>
                          ))}
                          {Diff.length === 0 && (
                            <li className="text-muted">
                              This role does not have additional permissions for
                              this category.
                            </li>
                          )}
                        </ul>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                )
              )}
            </Accordion>
          </div>
        )}
        {isSuccess && Object.keys(groupFunction(union, diff)).length === 0 && (
          <div className="mt-2">
            <span>
              No additional {title.toLowerCase()} found for this role.
            </span>
          </div>
        )}
      </>
    )
  }

  const DataAccessAccordion = ({ data, groupFunction, title }) => {
    return (
      <>
        {isSuccess && Object.keys(groupFunction(data)).length > 0 && (
          <div className="mt-2">
            <Accordion alwaysOpen>
              {Object.entries(groupFunction(data)).map(
                ([topModule, names], index) => (
                  <Accordion.Item eventKey={index.toString()} key={topModule}>
                    <Accordion.Header>
                      <FontAwesomeIcon icon={faFolder} className="me-2" />
                      {topModule}
                    </Accordion.Header>
                    <Accordion.Body>
                      {Object.entries(names).map(([name, items]) => (
                        <div key={name} className="mb-3">
                          <strong>{name}</strong>
                          <ul className="ps-3 list-disc">
                            {items.map((item, idx) => (
                              <li key={`${name}-${idx}`}>
                                Access Type:{' '}
                                {mapAccessType(municipality, item.access_type)}{' '}
                                Start: {item.start1}, End: {item.end1}
                                <div className="text-muted small">
                                  Program: {item.program.join(', ')}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                )
              )}
            </Accordion>
          </div>
        )}
        {isSuccess && Object.keys(groupFunction(data)).length === 0 && (
          <div className="mt-2">
            <span>
              No additional {title.toLowerCase()} found for this role.
            </span>
          </div>
        )}
      </>
    )
  }

  const EmployeesList = ({ id, groupedEmployees, diff }) => {
    return (
      <div className="my-3">
        {Object.entries(groupEmployeesByDepartment(groupedEmployees)).map(
          ([department, employees]) => (
            <div key={`${id}-${department}`} className="my-2 mt-4">
              <div>
                {department}
                <span className="text-muted small ms-2">
                  {employees.length} users
                </span>
              </div>
              <ul className={`my-2 ${diff ? 'triangle-bullets' : ''}`}>
                {employees.map((employee) => (
                  <li key={`list-${id}-${employee.id}`}>
                    {employee.external_id}
                  </li>
                ))}
              </ul>
            </div>
          )
        )}
      </div>
    )
  }

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3">
          <Modal.Title>Role Comparison</Modal.Title>
          {isSuccess && (
            <>
              {/* Keyed on the comparison so switching roles remounts it and the
                  default export name follows the pair being compared. */}
              <SodExport
                key={`role-comparison-export-${params.role_id}`}
                params={exportParams}
                defaultName={`Role Comparison - ${role.name} vs ${data.comparison.name}`}
                source="roleComparison"
                size="sm"
              />
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="overlapping-permissions"
                  checked={modalOptions.showOverlappingPermissions}
                  onChange={() =>
                    setModalOptions({
                      ...modalOptions,
                      showOverlappingPermissions:
                        !modalOptions.showOverlappingPermissions
                    })
                  }
                />
                <label
                  className="form-check-label small text-muted"
                  htmlFor="overlapping-permissions"
                >
                  Include overlapping permissions
                </label>
              </div>
            </>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row className="sticky-top bg-white py-2 border-bottom">
          <Col sm={6} className="border-end">
            <div className="mt-2 fw-bold">{role.name}</div>
            {!params.role_id && (
              <div className="text-muted">
                <span>Select a role to compare with {role.name}</span>
              </div>
            )}
          </Col>
          <Col sm={6}>
            <RoleDropdown setFilterParams={setParams} filterParams={params} />

            {!params.role_id && (
              <div className="mt-2">
                {role.similar_roles.length > 0 && (
                  <>
                    <div className="text-muted">
                      Here are similar roles we found
                    </div>
                    {role.similar_roles.map((similarRole) => (
                      <button
                        key={similarRole.id}
                        className="btn btn-outline-primary btn-sm ms-2 text-dark fw-bold rounded-4 my-1"
                        onClick={() =>
                          setParams({
                            ...params,
                            role_id: similarRole.comparison_role_id
                          })
                        }
                      >
                        {similarRole.comparison_role}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </Col>
        </Row>

        {!!params.role_id && (
          <>
            <Row>
              <Col sm={6} className="border-end">
                {isSuccess && <SummaryTable stats={stats(data.role)} />}
              </Col>
              <Col sm={6}>
                {isSuccess && <SummaryTable stats={stats(data.comparison)} />}
              </Col>
            </Row>

            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Users
            </div>

            <Row className="mt-2 border-top p-2">
              <Col sm={12}>
                <div className="fw-bold text-center underline mb-2">
                  Users with access to both roles
                </div>
                {isSuccess && data.role.employees_union.length === 0 && (
                  <div className="text-muted text-center">No users found</div>
                )}
              </Col>
              <Col sm={6} className="border-end">
                {isSuccess && data.role.employees_union.length > 0 && (
                  <EmployeesList
                    groupedEmployees={data.role.employees_union}
                    key={`role-union-${data.role.id}`}
                    id={`role-union-${data.role.id}`}
                    diff={false}
                  />
                )}
              </Col>
              <Col sm={6}>
                {isSuccess && data.role.employees_union.length > 0 && (
                  <EmployeesList
                    groupedEmployees={data.role.employees_union}
                    key={`comparison-union-${data.comparison.id}`}
                    id={`comparison-union-${data.comparison.id}`}
                    diff={false}
                  />
                )}
              </Col>
            </Row>
            <Row className="mt-2 border-top p-2">
              <Col sm={12}>
                <div className="fw-bold text-center underline mb-2">
                  Users with access to only one role
                </div>
                {isSuccess &&
                  data.role.employees_diff.length === 0 &&
                  data.comparison.employees_diff === 0 && (
                    <div className="text-muted text-center">No users found</div>
                  )}
              </Col>

              {isSuccess &&
                (data.role.employees_diff.length > 0 ||
                  data.comparison.employees_diff.length > 0) && (
                  <>
                    <Col sm={6} className="border-end">
                      <EmployeesList
                        groupedEmployees={data.role.employees_diff}
                        key={`role-diff-${data.role.id}`}
                        id={`role-diff-${data.role.id}`}
                        diff={true}
                      />
                    </Col>
                    <Col sm={6}>
                      <EmployeesList
                        groupedEmployees={data.comparison.employees_diff}
                        key={`comparison-diff-${data.comparison.id}`}
                        id={`comparison-diff-${data.comparison.id}`}
                        diff={true}
                      />
                    </Col>
                  </>
                )}
            </Row>

            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Permissions
            </div>
            {isSuccess && (
              <Row className="mt-2 border-top p-2">
                <Col sm={6} className="border-end">
                  <PermissionsAccordion
                    diff={data.role.permissions_diff}
                    union={data.role.permissions_union}
                    groupFunction={groupAndSortPermissionsByCategory}
                    title="Permissions"
                    showOverlapping={modalOptions.showOverlappingPermissions}
                  />
                </Col>
                <Col sm={6}>
                  <PermissionsAccordion
                    diff={data.comparison.permissions_diff}
                    union={data.role.permissions_union}
                    groupFunction={groupAndSortPermissionsByCategory}
                    title="Permissions"
                    showOverlapping={modalOptions.showOverlappingPermissions}
                  />
                </Col>
              </Row>
            )}
            <div
              className="p-2 mt-3 rounded-1"
              style={{ backgroundColor: '#f9f9f9' }}
            >
              Data Access
            </div>
            {isSuccess && (
              <Row className="mt-2 border-top p-2">
                <Col sm={6} className="border-end">
                  <DataAccessAccordion
                    data={data.role.data_access}
                    groupFunction={groupDataAccessByTopModuleAndName}
                    title="Data Access"
                  />
                </Col>
                <Col sm={6}>
                  <DataAccessAccordion
                    data={data.comparison.data_access}
                    groupFunction={groupDataAccessByTopModuleAndName}
                    title="Data Access"
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CompareRoles
